import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Machine } from '../type/Machine';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { MachineManagerPanel } from './MachineManagerPanel';

type MachineManagerModalProps = {
  isOpen: boolean;
  companyName: string;
  onClose: () => void;
  onSave: (machine?: Machine, action?: 'add' | 'update' | 'delete') => void;
  selectedMachineName?: string | null;
};

export const MachineManagerModal: React.FC<MachineManagerModalProps> = ({
  isOpen,
  companyName,
  onClose,
  onSave,
  selectedMachineName,
}) => {
  const { t } = useTranslation();
  const http = useApiClient();
  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-full max-w-[calc(100%-2rem)] sm:max-w-[95vw] rounded-3xl border bg-card/95 p-0 shadow-2xl backdrop-blur-sm xl:max-w-6xl"
        showCloseButton={true}
      >
        <div className="flex max-h-[90vh] flex-col">
          <DialogHeader className="space-y-2 px-6 pt-6 text-left">
            <DialogTitle className="text-2xl font-semibold text-foreground">
              {t('traceability.machineManager.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('traceability.machineManager.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <MachineManagerPanel
              companyName={companyName}
              traceabilityApiClient={traceabilityApiClient}
              onSave={onSave}
              selectedMachineName={selectedMachineName}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
